/**
 * util ajax module
 * @module util/ajax
 */
import { buildUrl } from './url';
import { isObject } from './type';

/**
 * Update an Error with the specified config, error code, and response.
 *
 * @param {Error} error The error to update.
 * @param {Object} config The config.
 * @param {Object} [request] The request.
 * @param {Object} [response] The response.
 * @returns {Error} The error.
 */
/* eslint-disable-next-line */
const createReqError = (message, config, request, response) => {
  const error = new Error(message);
  error.config = config;
  error.request = request;
  error.response = response;
  return error;
};

const requestDefaultConfig = {
  method: 'GET',
  timeout: 10000,
  withCredentials: false,
  withGTK: true,
};

const doJsonp = reqConfig => new Promise((resolve, reject) => {
  const callback = reqConfig.jsonp || 'callback';
  const callbackName = reqConfig.jsonpCallback || `jsonpCallback${Date.now()}`;
  let headEl = document.querySelector('head');
  let scriptEl = document.createElement('script');

  window[callbackName] = (jRes) => {
    if (jRes) {
      resolve(jRes);
    } else {
      reject(jRes);
    }
    headEl.removeChild(scriptEl);
    delete window[callbackName];
    headEl = null;
    scriptEl = null;
  };

  scriptEl.src = `${buildUrl(reqConfig.url, reqConfig.params, reqConfig.withGTK)}&${callback}=${callbackName}`;
  scriptEl.onerror = (err) => {
    reject(err);
  };
  headEl.appendChild(scriptEl);
});

const handleReadyStateChange = (xhr, reqConfig, resolve, reject) => {
  if (!xhr || xhr.readyState !== 4) {
    return;
  }
  if (xhr.status === 0 && !(xhr.responseURL && xhr.responseURL.indexOf('file:') === 0)) {
    return;
  }

  const responseData = !reqConfig.responseType || reqConfig.responseType === 'text'
    ? xhr.responseText
    : xhr.response;
  if (xhr.status === 200) {
    resolve(responseData);
  } else {
    reject(xhr);
  }
  /* eslint-disable */
  xhr = null;
};

/**
 * send ajax
 * @param {Object} config request config
 * @param {String} config.url request url
 * @param {String} [config.method] request method, default 'get'
 * @param {Object} [config.params] request params
 * @param {Object} [config.data] request data
 * @param {Number} [config.timeout] request timeout
 * @param {Object} [config.headers] request headers
 * @param {Boolean} [config.withCredentials] request with credentials, default false
 * @param {Boolean} [config.withGTK] request with g_tk params, default true
 * @param {String} [config.jsonp] jsonp callback param name, default 'callback'
 * @param {Boolean} [config.jsonpCallback] jsonp callback name, default jsonpCallback + Date.now()
 * @param {String} [config.responseType] request responseType
 * @param {Function} [config.onDownloadProgress] download pregress function
 * @param {Function} [config.onUploadProgress] upload pregress function
 * @returns {Promise}
 * @example
 * import WSLib from '@tencent/weishi-lib';
 * WSLib.wsUtil.ajax.request({url: 'https://xxx.xxx.com/api/get'})
 * .then(res => console.log(res))
 * .catch(err => console.error(err));
 */
export function request(config) {
  if (typeof config !== 'object' || !config.url) {
    throw new TypeError('request params is invalid');
  }
  const reqConfig = Object.assign({}, requestDefaultConfig, config);
  if (reqConfig.method.toUpperCase() === 'JSONP') {
    return doJsonp(reqConfig);
  }
  return new Promise((resolve, reject) => {
    let requestData = reqConfig.data;

    const requestHeaders = reqConfig.headers;
    if (typeof FormData !== 'undefined' && requestData instanceof FormData) {
      if (isObject(requestHeaders)) {
        delete requestHeaders['Content-Type']; // Let the browser set it
      }
    }

    let xhr = new XMLHttpRequest();
    xhr.open(reqConfig.method.toUpperCase(), buildUrl(reqConfig.url, reqConfig.params, reqConfig.withGTK), true);

    // Set the request timeout in MS
    xhr.timeout = reqConfig.timeout;

    // Listen for ready state
    xhr.onreadystatechange = () => handleReadyStateChange(xhr, reqConfig, resolve, reject);

    // Handle browser request cancellation (as opposed to a manual cancellation)
    xhr.onabort = () => {
      if (!xhr) {
        return;
      }
      reject();
      xhr = null;
    };

    // Handle low level network errors
    xhr.onerror = () => {
      reject(createReqError('Network Error', reqConfig, xhr));
    };

    // Handle timeout
    xhr.ontimeout = () => {
      reject(createReqError(`timeout of ${reqConfig.timeout}ms exceeded`, reqConfig, xhr));
    };

    // Add headers to the request
    if ('setRequestHeader' in xhr && typeof requestHeaders === 'object') {
      Object.keys(requestHeaders).forEach((key) => {
        if (typeof requestData === 'undefined' && key.toLowerCase() === 'content-type') {
          // Remove Content-Type if data is undefined
          delete requestHeaders[key];
        } else {
          // Otherwise add header to the request
          xhr.setRequestHeader(key, requestHeaders[key]);
        }
      });
    }

    // Add withCredentials to request if needed
    if (reqConfig.withCredentials) {
      xhr.withCredentials = true;
    }

    // Add responseType to request if needed
    if (reqConfig.responseType) {
      try {
        xhr.responseType = reqConfig.responseType;
      } catch (e) {
        // Expected DOMException thrown by browsers not compatible XMLHttpRequest Level 2.
        // But, this can be suppressed for 'json' type as it can be parsed by default 'transformResponse' function.
        if (reqConfig.responseType !== 'json') {
          throw e;
        }
      }
    }

    // Handle progress if needed
    if (typeof reqConfig.onDownloadProgress === 'function') {
      xhr.addEventListener('progress', reqConfig.onDownloadProgress);
    }

    // Not all browsers support upload events
    if (typeof reqConfig.onUploadProgress === 'function' && xhr.upload) {
      xhr.upload.addEventListener('progress', reqConfig.onUploadProgress);
    }

    if (requestData === undefined) {
      requestData = null;
    }

    // Send the xhr
    xhr.send(requestData);
  });
}

export default {
  request,
};
